/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React, { FC, PropsWithChildren, useEffect, useRef, useState } from 'react';
import { Stack, styled } from '@mui/material';

import { Button } from '../../Button';
import { Modal } from '../../Modal';
import { CartItem } from '../../../features/cart/storage/cart.types';
import TestIds from '../../../testIds';
import {
  CartProductCard,
  CartProductCardProps,
} from '../../../features/products/components/ProductCard/CartProductCard';
import { addGST } from '../../../util/addGst';
import {
  TUnitPriceFormData,
  IUnitPriceFormRef,
  IUnitPriceState,
  UnitPriceForm,
} from '../../forms/UnitPriceForm/UnitPriceForm';
import { isPriceDifferent } from '../../../util/formatting/format-price';

export interface IEditedCartItemDetails {
  editedPrice: number;
  editedPriceReasonCode: string | undefined;
  editedPriceReasonNote: string | undefined;
  editedQuantity: number;
  item: CartItem;
}

const StyledButton = styled(Button)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));

export interface IEditPriceModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  item?: CartItem;
  onApplyEditedPrice: (editedItemDetails: IEditedCartItemDetails) => void;
  onRevertEditedPrice: (item: CartItem) => void;
  cartProductCardProps?: Omit<CartProductCardProps, 'item'>;
}

export const EditPriceModal: FC<IEditPriceModalProps> = ({
  open,
  setOpen,
  item,
  onApplyEditedPrice,
  onRevertEditedPrice,
  cartProductCardProps,
}: PropsWithChildren<IEditPriceModalProps>): JSX.Element | null => {
  const unitPriceFormRef = useRef<IUnitPriceFormRef>(null);

  const [unitPrice, setUnitPrice] = useState<number | undefined>();
  const [quantity, setQuantity] = React.useState(item?.quantity ?? 0);
  const [editedTotalPrice, setEditedTotalPrice] = React.useState(
    item?.editedPriceExcGst && item?.quantity ? item.editedPriceExcGst * item.quantity : undefined,
  );

  useEffect(() => {
    if (item) {
      setUnitPrice(item.discountedPriceExcGst / item.quantity);
      setEditedTotalPrice(
        item?.editedPriceExcGst ? item?.editedPriceExcGst * item.quantity : undefined,
      );
      setQuantity(item?.quantity ?? 0);
    }
  }, [item, open]);

  const handleCloseModal = (): void => setOpen(false);

  const handleUnitPriceFormChange = (unitPriceState: IUnitPriceState | undefined): void => {
    setEditedTotalPrice(unitPriceState?.unitPrice ? unitPriceState?.unitPrice * quantity : 0);
  };

  const handleUnitPriceFormRevertChanges = (): void => {
    if (item) {
      setUnitPrice(undefined);
      onRevertEditedPrice(item);
    }

    handleCloseModal();
  };

  const applyEditedPrice = async (): Promise<void> => {
    await unitPriceFormRef.current?.submit();
  };

  const handleUnitPriceFormSubmit = (formData: TUnitPriceFormData): void => {
    const newPrice: number = parseFloat(formData.unitPrice || '0');
    const unitPriceChanged: boolean | undefined = isPriceDifferent(newPrice, unitPrice || 0);
    const reasonChanged: boolean =
      !!(item?.lastEditedReasonCode && formData.reasonCode !== item?.lastEditedReasonCode) ||
      !!(item?.lastEditedReasonNote && formData.reasonNote !== item?.lastEditedReasonNote);
    const shouldUpdateReason: boolean = unitPriceChanged || reasonChanged;

    if (shouldUpdateReason || item?.quantity !== quantity) {
      onApplyEditedPrice({
        editedPrice: newPrice,
        editedPriceReasonCode: (shouldUpdateReason && formData.reasonCode) || undefined,
        editedPriceReasonNote: (shouldUpdateReason && formData.reasonNote) || undefined,
        editedQuantity: quantity,
        item: item as CartItem,
      });
    }

    handleCloseModal();
  };

  const handleCartProductCardChangeQuantity = (value: number): void => {
    if (!value) {
      setUnitPrice(undefined);
      setEditedTotalPrice(undefined);
    } else {
      setQuantity(value);

      const currentFormDataUnitPrice: string | undefined =
        unitPriceFormRef.current?.getFormData()?.unitPrice;
      const currentUnitPrice: number | undefined = currentFormDataUnitPrice
        ? parseFloat(currentFormDataUnitPrice)
        : unitPrice;

      if (currentUnitPrice) {
        setEditedTotalPrice(currentUnitPrice * value);
      }
    }
  };

  return item && open ? (
    <Modal
      title='Edit price'
      open={open}
      handleClose={handleCloseModal}
      footer={
        <StyledButton
          data-testid={TestIds.feat.editPrice.saveChanges}
          variant='contained'
          onClick={applyEditedPrice}
        >
          Save changes
        </StyledButton>
      }
      testId={TestIds.feat.editPrice.modal}
    >
      <Stack spacing={4} pb={2}>
        <CartProductCard
          item={{
            ...item,
            quantity: quantity,
            discountedPriceExcGst: editedTotalPrice ?? item.discountedPriceExcGst,
            discountedPriceIncGst: editedTotalPrice
              ? addGST(editedTotalPrice)
              : item.discountedPriceIncGst,
          }}
          onChangeQuantity={handleCartProductCardChangeQuantity}
          enableRemove={false}
          {...(cartProductCardProps || {})}
        />

        <UnitPriceForm
          ref={unitPriceFormRef}
          productId={item?.productId}
          unitPriceState={{
            unitPrice: unitPrice || undefined,
            reasonCode: item?.lastEditedReasonCode,
            reasonNote: item?.lastEditedReasonNote,
          }}
          validateAllFieldsOnSubmit={!!(item?.lastEditedReasonCode || item?.lastEditedReasonNote)}
          onFormChange={handleUnitPriceFormChange}
          onSubmit={handleUnitPriceFormSubmit}
          onRevertChanges={handleUnitPriceFormRevertChanges}
        />
      </Stack>
    </Modal>
  ) : null;
};
